import { useQuery } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  X,
  UserPlus,
  ChevronDown,
  ChevronRight,
  Mail,
  Users,
} from 'lucide-react';
import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import {
  downloadBulkInviteTemplate,
  validateBulkInvite,
  bulkInviteEmployees,
  getCorporateAvailablePrograms,
} from '@/apis/corporates';
import { withErrorBoundary } from '@/components/common/ErrorBoundary';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { logger } from '@/lib/logger';
import {
  BulkInviteValidationResult,
  BulkInviteResult,
  CorporateAvailableProgram,
} from '@/types/corporate';
import { getAccessToken } from '@/utils/auth';
import {
  BULK_INVITE_LARGE_BATCH_ROW_WARNING,
  getBulkInviteFileValidationError,
} from '@/utils/bulkInviteFileValidation';
import { extractSanitizedApiError } from '@/utils/sanitizeError';

const NO_PROGRAM_VALUE = '__none__';
const PREVIEW_PAGE_SIZE = 50;
const INVITE_DEBOUNCE_MS = 2000;
const BULK_INVITE_FILE_REQUIREMENTS_ID = 'bulk-invite-file-requirements';
const BULK_INVITE_FILE_INPUT_ID = 'bulk-invite-file-input';

const isAbortError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  return axios.isCancel(error);
};

interface BulkInviteEmployeesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  corporateId: string;
  corporateName: string;
}

type Step = 'upload' | 'preview' | 'inviting' | 'results';

export const BulkInviteEmployeesModalContent: React.FC<BulkInviteEmployeesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  corporateId,
  corporateName,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<BulkInviteValidationResult | null>(null);
  const [inviteResult, setInviteResult] = useState<BulkInviteResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [expandedErrorRows, setExpandedErrorRows] = useState<Set<number>>(new Set());
  const [expandedFailedRows, setExpandedFailedRows] = useState<Set<number>>(new Set());
  const [sendEmail, setSendEmail] = useState(true);
  const [selectedProgramId, setSelectedProgramId] = useState<string>(NO_PROGRAM_VALUE);
  const [previewPage, setPreviewPage] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastInviteTimeRef = useRef(0);
  const hasUserSelectedProgramRef = useRef(false);
  const hasAutoSelectedProgramRef = useRef(false);
  const isMountedRef = useRef(true);

  const { data: availableProgramsData, isLoading: isLoadingPrograms } = useQuery({
    queryKey: ['corporate-available-programs', corporateId, isOpen],
    queryFn: async () => {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) throw new Error('No authentication token');
      return getCorporateAvailablePrograms(corporateId, token, { is_active: true });
    },
    enabled: isOpen && !!corporateId,
  });

  const availablePrograms: CorporateAvailableProgram[] = useMemo(() => {
    if (!availableProgramsData) return [];
    return Array.isArray(availableProgramsData)
      ? availableProgramsData
      : availableProgramsData.results || [];
  }, [availableProgramsData]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    hasUserSelectedProgramRef.current = false;
    hasAutoSelectedProgramRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (
      !isOpen ||
      isLoadingPrograms ||
      availablePrograms.length === 0 ||
      hasUserSelectedProgramRef.current ||
      hasAutoSelectedProgramRef.current
    ) {
      return;
    }

    const defaultProgram = availablePrograms.find((program) => program.is_default);
    if (defaultProgram) {
      hasAutoSelectedProgramRef.current = true;
      setSelectedProgramId(defaultProgram.program);
      const programName =
        defaultProgram.program_details?.name || defaultProgram.program_name || 'Program';
      toast.info(`Default program "${programName}" selected for this batch.`);
    }
  }, [availablePrograms, isOpen, isLoadingPrograms]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const resetModalState = useCallback(() => {
    setStep('upload');
    setSelectedFile(null);
    setValidationResult(null);
    setInviteResult(null);
    setIsLoading(false);
    setExpandedErrorRows(new Set());
    setExpandedFailedRows(new Set());
    setSendEmail(true);
    setSelectedProgramId(NO_PROGRAM_VALUE);
    setPreviewPage(0);
    hasUserSelectedProgramRef.current = false;
    hasAutoSelectedProgramRef.current = false;
  }, []);

  const handleClose = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    resetModalState();
    onClose();
  }, [onClose, resetModalState]);

  const toggleErrorRowExpansion = useCallback((rowNumber: number) => {
    setExpandedErrorRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowNumber)) {
        newSet.delete(rowNumber);
      } else {
        newSet.add(rowNumber);
      }
      return newSet;
    });
  }, []);

  const toggleFailedRowExpansion = useCallback((index: number) => {
    setExpandedFailedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) {
        throw new Error('No authentication token available');
      }

      const blob = await downloadBulkInviteTemplate(token);

      const url = window.URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = 'employee_invite_template.xlsx';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        if (link.parentNode === document.body) {
          document.body.removeChild(link);
        }
        toast.success('Template downloaded successfully');
      } finally {
        window.URL.revokeObjectURL(url);
      }
    } catch (error: any) {
      logger.error('Template download error:', error);
      toast.error(error.message || 'Failed to download template');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    const validationError = getBulkInviteFileValidationError(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSelectedFile(file);
  };

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile || isLoading) return;

    setIsLoading(true);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) {
        throw new Error('No authentication token available');
      }

      const result = await validateBulkInvite(
        selectedFile,
        corporateId,
        token,
        abortControllerRef.current.signal
      );

      if (!isMountedRef.current) return;

      if (result.total_rows === 0) {
        setValidationResult(null);
        toast.error('No employees found in the file. Please ensure data starts from row 2.');
        return;
      }

      setValidationResult(result);
      setPreviewPage(0);
      setStep('preview');

      if (result.total_rows > BULK_INVITE_LARGE_BATCH_ROW_WARNING) {
        toast.warning(
          `This file contains ${result.total_rows} rows. Large batches may take longer to process.`
        );
      }

      if (result.has_errors) {
        toast.error(`Found ${result.error_count} errors. Please review and fix them.`);
      } else {
        toast.success(`All ${result.valid_count} employees are valid and ready to invite.`);
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        return;
      }
      logger.error('Validation error:', error);
      if (isMountedRef.current) {
        const errorMessage = extractSanitizedApiError(error, 'Failed to validate file');
        toast.error(
          `Validation failed: ${errorMessage}. Please check the file format and try again.`
        );
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleInvite = async () => {
    if (!selectedFile || isLoading) return;

    const now = Date.now();
    if (now - lastInviteTimeRef.current < INVITE_DEBOUNCE_MS) {
      toast.error(`Please wait ${INVITE_DEBOUNCE_MS / 1000} seconds between invitations`);
      return;
    }
    lastInviteTimeRef.current = now;

    setIsLoading(true);
    setStep('inviting');
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();

    try {
      const session = await fetchAuthSession();
      const token = getAccessToken(session);
      if (!token) {
        throw new Error('No authentication token available');
      }

      const result = await bulkInviteEmployees(
        selectedFile,
        corporateId,
        token,
        sendEmail,
        selectedProgramId === NO_PROGRAM_VALUE ? undefined : selectedProgramId,
        abortControllerRef.current.signal
      );
      if (!isMountedRef.current) return;

      setInviteResult(result);
      setStep('results');

      if (result.success_count > 0) {
        toast.success(
          `Successfully invited ${result.success_count} employees${
            result.failure_count > 0 ? ` (${result.failure_count} failed)` : ''
          }.`
        );
        onSuccess?.();
      } else {
        toast.error('No employees were invited. Please check the errors.');
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        if (isMountedRef.current) {
          setStep('preview');
        }
        return;
      }
      logger.error('Invite error:', error);
      if (isMountedRef.current) {
        const errorMessage = extractSanitizedApiError(error, 'Failed to invite employees');
        toast.error(`${errorMessage}. Please review the file and try again.`);
        setStep('preview');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  const handleBackToUpload = () => {
    setStep('upload');
    setSelectedFile(null);
    setValidationResult(null);
    setExpandedErrorRows(new Set());
    setPreviewPage(0);
  };

  const paginatedPreviewRows = useMemo(() => {
    if (!validationResult) {
      return [];
    }

    const allRows = [
      ...validationResult.valid_employees.map((employee) => ({
        type: 'valid' as const,
        employee,
      })),
      ...validationResult.validation_errors.map((error) => ({
        type: 'error' as const,
        error,
      })),
    ];
    const start = previewPage * PREVIEW_PAGE_SIZE;
    return allRows.slice(start, start + PREVIEW_PAGE_SIZE);
  }, [validationResult, previewPage]);

  const totalPreviewPages = useMemo(() => {
    if (!validationResult) return 0;
    const totalRows =
      validationResult.valid_employees.length + validationResult.validation_errors.length;
    return Math.ceil(totalRows / PREVIEW_PAGE_SIZE);
  }, [validationResult]);

  const renderUploadStep = () => (
    <div className="space-y-4">
      <Alert>
        <Users className="h-4 w-4" />
        <AlertDescription>
          Inviting employees to <strong>{corporateName}</strong>. Download the template, fill in
          employee details, and upload for validation.
        </AlertDescription>
      </Alert>

      <div className="flex justify-center">
        <Button
          onClick={handleDownloadTemplate}
          variant="outline"
          className="w-full sm:w-auto"
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Downloading...
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </>
          )}
        </Button>
      </div>

      <div className="relative">
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload employee spreadsheet"
          aria-describedby={BULK_INVITE_FILE_REQUIREMENTS_ID}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openFilePicker();
            }
          }}
          className={`
            border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
            transition-colors duration-200
            ${isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400'}
          `}
          onClick={openFilePicker}
        >
          <input
            id={BULK_INVITE_FILE_INPUT_ID}
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileInputChange}
            className="hidden"
            aria-describedby={BULK_INVITE_FILE_REQUIREMENTS_ID}
          />

          <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />

          {selectedFile ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-green-600" />
                <span className="font-medium">{selectedFile.name}</span>
              </div>
              <p className="text-sm text-gray-500">{(selectedFile.size / 1024).toFixed(2)} KB</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                }}
              >
                <X className="h-4 w-4 mr-1" />
                Remove
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-lg font-medium text-gray-700">Drag and drop your file here</p>
              <p className="text-sm text-gray-500">or click to browse</p>
              <p id={BULK_INVITE_FILE_REQUIREMENTS_ID} className="text-xs text-gray-400">
                Accepts .xlsx, .xls, and .csv files up to 10MB
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleValidate} disabled={!selectedFile || isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Validating...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Validate & Preview
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!validationResult) return null;

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-700">{validationResult.total_rows}</div>
            <div className="text-sm text-blue-600">Total Rows</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-700">
              {validationResult.new_invites_count || 0}
            </div>
            <div className="text-sm text-green-600">New Invites</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-700">
              {validationResult.existing_users_count || 0}
            </div>
            <div className="text-sm text-orange-600">Already Enrolled</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-700">{validationResult.error_count}</div>
            <div className="text-sm text-red-600">Errors</div>
          </div>
        </div>

        {validationResult.existing_users_count > 0 && (
          <Alert>
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription>
              {validationResult.existing_users_count} employee(s) are already enrolled and will be
              skipped.
            </AlertDescription>
          </Alert>
        )}

        {validationResult.total_rows > BULK_INVITE_LARGE_BATCH_ROW_WARNING && (
          <Alert>
            <AlertCircle className="h-4 w-4 text-orange-600" />
            <AlertDescription>
              This file contains {validationResult.total_rows} rows. Large batches may take longer
              to process.
            </AlertDescription>
          </Alert>
        )}

        {validationResult.has_errors && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Found {validationResult.error_count} errors. Fix them before processing or proceed
              with valid entries only.
            </AlertDescription>
          </Alert>
        )}

        <ScrollArea className="h-[350px] border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Row</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedPreviewRows.map((row) => {
                if (row.type === 'valid') {
                  const employee = row.employee;
                  return (
                    <TableRow key={`valid-${employee.row}`}>
                      <TableCell>{employee.row}</TableCell>
                      <TableCell>
                        {employee.data.first_name} {employee.data.last_name}
                      </TableCell>
                      <TableCell>{employee.data.email}</TableCell>
                      <TableCell>{employee.data.department || '-'}</TableCell>
                      <TableCell>
                        {employee.existing_user ? (
                          <Badge className="bg-orange-100 text-orange-800">Already Enrolled</Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800">
                            <UserPlus className="h-3 w-3 mr-1" />
                            New Invite
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }

                const error = row.error;
                const isExpanded = expandedErrorRows.has(error.row);
                return (
                  <React.Fragment key={`error-${error.row}`}>
                    <TableRow
                      className="bg-red-50 hover:bg-red-100 cursor-pointer"
                      onClick={() => toggleErrorRowExpansion(error.row)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          {error.row}
                        </div>
                      </TableCell>
                      <TableCell>
                        {error.data.first_name} {error.data.last_name}
                      </TableCell>
                      <TableCell>{error.data.email}</TableCell>
                      <TableCell>{error.data.department || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          {error.errors?.length || 0} Error
                          {(error.errors?.length || 0) > 1 ? 's' : ''}
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {isExpanded && error.errors && (
                      <TableRow className="bg-red-50">
                        <TableCell colSpan={5}>
                          <div className="p-3 bg-white rounded border-l-4 border-red-500">
                            <div className="text-sm font-semibold text-red-800 mb-2">
                              <AlertCircle className="h-4 w-4 inline mr-2" />
                              Validation Errors:
                            </div>
                            <div className="space-y-1">
                              {error.errors.map((err, idx) => (
                                <div key={idx} className="text-sm text-red-700 pl-6">
                                  • {err}
                                </div>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>

        {totalPreviewPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {previewPage + 1} of {totalPreviewPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={previewPage === 0}
                onClick={() => setPreviewPage((page) => page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={previewPage >= totalPreviewPages - 1}
                onClick={() => setPreviewPage((page) => page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="bulk-invite-program">Program (optional)</Label>
          <Select
            value={selectedProgramId}
            onValueChange={(value) => {
              hasUserSelectedProgramRef.current = true;
              setSelectedProgramId(value);
            }}
          >
            <SelectTrigger id="bulk-invite-program">
              <SelectValue placeholder="Select a program to assign to everyone" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROGRAM_VALUE}>No program (invite only)</SelectItem>
              {isLoadingPrograms ? (
                <SelectItem value="loading" disabled>
                  Loading programs...
                </SelectItem>
              ) : (
                availablePrograms.map((program) => (
                  <SelectItem key={program.id} value={program.program}>
                    {program.program_details?.name || program.program_name || 'Program'}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500">
            The selected program is assigned to every invited employee in this batch.
          </p>
        </div>

        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
          <Checkbox
            id="send-email"
            checked={sendEmail}
            onCheckedChange={(checked) => setSendEmail(checked === true)}
          />
          <Label htmlFor="send-email" className="flex items-center gap-2 cursor-pointer">
            <Mail className="h-4 w-4" />
            Send invitation emails to employees
          </Label>
        </div>

        <div className="flex justify-between">
          <Button variant="outline" onClick={handleBackToUpload}>
            Back to Upload
          </Button>
          <Button onClick={handleInvite} disabled={validationResult.valid_count === 0 || isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Invite {validationResult.new_invites_count || validationResult.valid_count} Employee
                {(validationResult.new_invites_count || validationResult.valid_count) > 1
                  ? 's'
                  : ''}
                {validationResult.error_count > 0 && ` (Skip ${validationResult.error_count})`}
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  const renderInvitingStep = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-4">
      <Loader2 className="h-16 w-16 animate-spin text-primary" />
      <p className="text-lg font-medium">Inviting employees...</p>
      <p className="text-sm text-gray-500">
        Creating accounts and sending invitation emails. This may take a moment.
      </p>
    </div>
  );

  const renderResultsStep = () => {
    if (!inviteResult) return null;

    const successResults = inviteResult.results.filter((r) => r.status === 'success');
    const failedResults = inviteResult.results.filter((r) => r.status === 'error');

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-700">{inviteResult.success_count}</div>
            <div className="text-sm text-green-600">Invited</div>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-orange-700">
              {inviteResult.already_enrolled_count || 0}
            </div>
            <div className="text-sm text-orange-600">Already Enrolled</div>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <div className="text-2xl font-bold text-red-700">{inviteResult.failure_count}</div>
            <div className="text-sm text-red-600">Failed</div>
          </div>
        </div>

        {inviteResult.success_count > 0 && (
          <Alert>
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription>
              {inviteResult.success_count} employee(s) invited successfully.
              {sendEmail && ' Invitation emails have been sent.'}
            </AlertDescription>
          </Alert>
        )}

        {inviteResult.failure_count > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {inviteResult.failure_count} invitation(s) failed. Review errors below.
            </AlertDescription>
          </Alert>
        )}

        <ScrollArea className="h-[350px] border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Row</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {successResults.map((result) => (
                <TableRow key={result.row}>
                  <TableCell>{result.row}</TableCell>
                  <TableCell>
                    {result.first_name} {result.last_name}
                  </TableCell>
                  <TableCell>{result.email}</TableCell>
                  <TableCell>
                    {result.already_enrolled ? (
                      <Badge className="bg-orange-100 text-orange-800">Already Enrolled</Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Invited
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {failedResults.map((result, idx) => {
                const isExpanded = expandedFailedRows.has(idx);
                return (
                  <React.Fragment key={result.row}>
                    <TableRow
                      className="bg-red-50 hover:bg-red-100 cursor-pointer"
                      onClick={() => toggleFailedRowExpansion(idx)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          {result.row}
                        </div>
                      </TableCell>
                      <TableCell>
                        {result.first_name} {result.last_name}
                      </TableCell>
                      <TableCell>{result.email}</TableCell>
                      <TableCell>
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Failed
                        </Badge>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow className="bg-red-50">
                        <TableCell colSpan={4}>
                          <div className="p-3 bg-white rounded border-l-4 border-red-500">
                            <div className="text-sm font-semibold text-red-800 mb-1">
                              Error Details:
                            </div>
                            <div className="text-sm text-red-700">
                              {result.error || 'Unknown error occurred'}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </ScrollArea>

        <div className="flex justify-end">
          <Button onClick={handleClose}>Close</Button>
        </div>
      </div>
    );
  };

  const getDialogTitle = () => {
    switch (step) {
      case 'upload':
        return 'Bulk Invite Employees';
      case 'preview':
        return 'Review & Validate';
      case 'inviting':
        return 'Sending Invitations';
      case 'results':
        return 'Invitation Results';
      default:
        return 'Bulk Invite Employees';
    }
  };

  const getDialogDescription = () => {
    switch (step) {
      case 'upload':
        return `Upload a file to invite multiple employees to ${corporateName}`;
      case 'preview':
        return 'Review the employees before sending invitations';
      case 'inviting':
        return 'Please wait while we process the invitations';
      case 'results':
        return 'Summary of the bulk invitation';
      default:
        return '';
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="corporateBulkInvite_dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="corporateBulkInvite_title">{getDialogTitle()}</DialogTitle>
          <DialogDescription>{getDialogDescription()}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'inviting' && renderInvitingStep()}
          {step === 'results' && renderResultsStep()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const BulkInviteEmployeesModal = withErrorBoundary(BulkInviteEmployeesModalContent);
